// @ts-ignore
// 按照ssr侧建议改为通过script标签引入，与ssr侧确认： html模版中下载文件，在组件客户端js 执行前就会加载完成，时机会比动态引入早，改为script标签引入更合理
// import * as marsWebloc from "@mtfe/mars-webloc/es";
import { APP_VERSION, CODE_ENV, CURRENT_CODE_ENV, IP_LOCATE_KEY, isServer } from "@/lib/constant";
import { getUrlParams } from "./urlParams";
import { Level, reportCustomMetric, reportError, reportPerfMetric } from "./owl";
import { getAppEnv } from "./getBrowserEnv";
import { checkCityIdInvalid } from "./utils";
import { fetchIPLocation, fetchCityInfoByLatLng } from "@/api/location";
import { logError, logInfo, logWarn } from "./logan";
import { ENABLE_SSR_COORD_LOCATE } from "@/lib/constant";

/**
 * 获取 mars-webloc 模块（同步）
 * SSR兼容性：服务端返回 null
 */
function getMarsWeblocSync() {
  return isServer ? null : window?.webloc;
}

export enum LocationSource {
  HTML = "html", // 浏览器定位
  NETWORK = "network", // CSR IP 定位
  APP = "app", // APP 环境使用 KNB 桥方法定位
  WX = "wx", // 微信环境定位
  DEFAULT = "default", // 兜底
  IP = "ip", // SSR IP定位
  COORD = "coord", // SSR 经纬度接口定位
}

interface LocationGeo {
  accuracy: number; // 35
  geotype: string; // "WGS84"
  latitude: number; // 39.985354
  longitude: number; // 116.459458
  source: LocationSource; // "html"
}

interface LocationCity {
  adcode: string; // "110105"
  address: string; // "花家地北里"
  areaId: string; // "-1"
  areaName: string; // ""
  city: string; // "北京"
  citycode: string; // "110100"
  country: string; // "中国"
  detail: string; // "花家地北里"
  detail_id: string; // ""
  detail_type: string; // ""
  detail_type_name: string; // ""
  district: string; // "朝阳区"
  formattedDetail: string; // "望京街道"
  formattedDetailId: string; // "1197828809739497540"
  formattedDetailType: string; // "2"
  id: number; // 1
  name: string; // "北京"
  pinyin: string; // ""
  province: string; // "北京市"
  provinceCode: string; // "110000"
  simpleDistrict: string; // "朝阳区"
  towncode: string; // "110105026"
  township: string; // "望京街道"
  type: string; // "meituan"
}

interface LocationData {
  city: LocationCity;
  geo: LocationGeo;
  detail: number[];
}

// https://docs.sankuai.com/mt/mars/mars-docs/master/websdk2/#getcityinfooptions-callback
const MARS_LOC_CONFIG = {
  key: "6a63dd679eb389c7126863858d967ba7",
  project: "mtunion-h5",
  prejectVersion: APP_VERSION,
  uuid: "",
  userid: "",
  debug: CURRENT_CODE_ENV !== CODE_ENV.PRODUCTION,
  env: "prod",
  timeout: getAppEnv().isBrowser ? 2500 : 2000, // 定位超时时间，定位超时后，会走保底策略network，cache
  htmlTimeout: 2500, // html定位策略超时时间
  enableHighAccuracy: true, // 采用html定位策略时,用来表明是否使用其最高精度来表示结果
  maximumAge: 0, // 采用html定位策略时,用来表明可以返回多长时间内的可获取的缓存位置
  KNBTimeout: getAppEnv().isIos ? 2 : 2000, // KNB定位超时时间。根据KNB文档，iOS 单位是秒，安卓是毫秒，用户需自行判断单位
  KNBReadyTimeout: 2000, // KNB.ready 超时时间。单位为 ms，默认为 3000
  KNBCache: true, // true时使用KNB缓存的地理位置信息，false时改为实时获取，KNB相关文档	默认为 false
  KNBSceneToken: "pt-2a95bfc8f1348e5a", // KNB定位依赖的标识参数sceneToken
  cacheTimeout: 300000, // 缓存过期时间。单位为 ms，默认为 300000
  geotype: "WGS84", // 定位结果的坐标系类型，可选值为 WGS84 或 GCJ02。默认为 WGS84
  city: true, // 是否需要城市信息
  cityType: "meituan", // 城市信息类型，美团、点评对应的城市信息id字段不一样，其他无区别。可选值有 [ 'meituan', 'dianping', 'all']，默认为 meituan。
  disable: ["html", "network"],
  enable: [],
  enableBottomline: true, // 是否启动保底策略	当enabel所设策略都失败后，是否使用network,cache保底，默认为true。
  urlMatchGroup: ["lat", "lng", "geotype"], // 纬度、经度和经纬度类型在 url query 中的 name,默认为 ['lat', 'lng', 'geotype']
  global: {}, // 用于传入 KNB 和 NX 对象,KNB 需要调用方提供，可以放在全局变量中（window.KNB = <knb>），也可以通过global 参数传入（global = { KNB: <knb> }）
  enableLogan: false, // Logan用于存储详细定位日志，当Logan发现定位不准确时，方便定位原因，但是会动态引用Logan标签。需要验证域名是否允许跨域。
  onlyIpLocation: false, // 是否仅使用 IP 定位。默认为false，设置成 true 后，将直接使用 IP 且只使用 IP 定位策略
};
// IP 定位与真实定位最大可接收误差（单位：公里）
export const CAMPARE_MAX_DISTANCE = 3;
// 兜底经纬度信息
export const DEFAULT_LOCATION: CityInfo = {
  cityId: 1,
  cityCode: "110000",
  townCode: "110102",
  cityName: "",
  lng: 116.411171,
  lat: 39.915478,
  wm_lng: 116411171,
  wm_lat: 39915478,
  source: LocationSource.DEFAULT,
};

export interface CityInfo {
  cityId: number; // 美团城市Id
  cityName: string; // 城市名称
  lng: number; // 经度
  lat: number; // 纬度
  wm_lng: number; // 长整数经度 = 经度 * 1e6
  wm_lat: number; // 长整数纬度 = 纬度 * 1e6
  cityCode?: string; // 二级城市编码
  townCode?: string; // 三级城市编码
  detailAddress?: string; // 详细地址
  source?: LocationSource; // 定位来源
}

type LocateError = "success" | "SDKNotAvailable" | "SDKFail";
function reportLocateSuccessRate(
  isSuccess: boolean,
  source: LocationSource | "none" = "none",
  errMsg: LocateError = "success",
) {
  reportCustomMetric({
    key: "locate",
    value: isSuccess ? 1 : 0,
    tags: {
      errMsg: errMsg,
      source,
    },
  });
}

function reportSSRLocateSuccessRate(
  hasSSRLocationInfo: boolean,
  {
    isSameActualCityId,
    isSameSelectCityId,
    isActualLatLngInRange,
    isSelectLatLngInRange,
    isSameTownCode,
  }: {
    isSameActualCityId: boolean;
    isSameSelectCityId: boolean;
    isActualLatLngInRange: boolean;
    isSelectLatLngInRange: boolean;
    isSameTownCode: boolean;
  },
) {
  reportCustomMetric({
    key: "ssrLocateSuccessRate",
    value: hasSSRLocationInfo ? 1 : 0,
    tags: {
      isSameActualCityId: isSameActualCityId ? 1 : 0,
      isSameSelectCityId: isSameSelectCityId ? 1 : 0,
      isActualLatLngInRange: isActualLatLngInRange ? 1 : 0,
      isSelectLatLngInRange: isSelectLatLngInRange ? 1 : 0,
      isSameTownCode: isSameTownCode ? 1 : 0,
      hasSSRLocationInfo: hasSSRLocationInfo ? 1 : 0,
    },
  });
}

/**
 * 位置信息类型
 */
export interface LocationInfo {
  actualCityInfo: CityInfo | null;
  selectCityInfo: CityInfo | null;
  isInited: boolean;
  source: LocationSource;
  // SSR 预取城市信息与当前城市信息的差异
  ssrDiff: SSRLocationDiff;
}

export interface SSRLocationDiff {
  isSameActualCityId: boolean; // 真实 cityId 是否相同
  isSameSelectCityId: boolean; // 手选 cityId 是否相同
  isActualLatLngInRange: boolean; // 真实经纬度是否在 3 公里内
  isSelectLatLngInRange: boolean; // 手选经纬度是否在 3 公里内
  isSameTownCode: boolean; // 三级城市编码 ID 是否相同
}

// 定位
export class LocationService {
  // 实际定位城市信息
  actualCityInfo: CityInfo | null = null;
  // 选择定位城市信息
  selectCityInfo: CityInfo | null = null;
  // IP定位城市信息
  ssrLocationInfo?: LocationInfo | null = null;
  // 获取定位的 Promise，在模板中提前开始获取定位，直接使用模板的 Promise
  currentLocationPromise: Promise<LocationData> | null = isServer
    ? null
    : window?.fxGetLocationPromise;

  // 真实定位 Promise
  actualCityInfoPromise: Promise<CityInfo> | null = null;
  // 选择城市 Promise
  selectCityInfoPromise: Promise<CityInfo> | null = null;

  init(ssrLocationInfo: LocationInfo | null = null) {
    // SSR兼容性：服务端渲染时跳过定位初始化
    if (isServer) {
      console.warn("[LocationService] Location initialization skipped on server side");
      return;
    }

    // 1. 保存 SSR 获取到的 IP 定位信息
    this.ssrLocationInfo = ssrLocationInfo || null;
    logInfo(`[LocationService] initLocation ssrLocationInfo: ${JSON.stringify(ssrLocationInfo)}`);
    // 2. 判断 SSR 已经获取了精确定位的信息，CSR 阶段不再获取定位信息
    if (
      this.ssrLocationInfo?.source === LocationSource.COORD && 
      this.ssrLocationInfo?.actualCityInfo && 
      this.ssrLocationInfo?.selectCityInfo
    ) {
      this.actualCityInfoPromise = Promise.resolve(this.ssrLocationInfo?.actualCityInfo);
      this.selectCityInfoPromise = Promise.resolve(this.ssrLocationInfo?.selectCityInfo);
      return;
    }
    // 3. 初始化时获取真实定位信息
    this.getActualCityInfo();
    // 4. 从 URL 上获取选择定位信息
    this.getSelectCityInfo();
  }

  /**
   * 获取当前位置信息
   * SSR兼容性：服务端渲染时返回rejected promise
   * @param config 可选配置，用于覆盖默认配置
   * @returns Promise<LocationData> 位置信息Promise
   */
  private getCurrentLocation(options?: {
    forceRefresh?: boolean;
    config?: Partial<typeof MARS_LOC_CONFIG>;
  }): Promise<LocationData> {
    const { forceRefresh = false, config = {} } = options || {};
    if (this.currentLocationPromise && !forceRefresh) {
      return this.currentLocationPromise;
    }

    this.currentLocationPromise = new Promise<LocationData>((resolve, reject) => {
      const webloc = getMarsWeblocSync();
      if (typeof webloc?.getCurrentPosition !== "function") {
        logError(`[LocationService] webloc.getCurrentPosition function not available`);
        reportLocateSuccessRate(false, "none", "SDKNotAvailable");
        reject("[LocationService] getCurrentPosition function not available");
        return;
      }

      const mergedConfig = { ...MARS_LOC_CONFIG, ...config };
      const getCurrentLocationStart = Date.now();
      webloc.getCurrentPosition(mergedConfig, (err: any, result: LocationData) => {
        const getCurrentLocationEnd = Date.now();
        if (err) {
          reject(`[LocationService] Error getting location: ${err.message || err}`);
          reportLocateSuccessRate(false, "none", "SDKFail");
          logError(`[LocationService] Error getting location: ${err.message || err}`);
        } else {
          resolve(result);
          reportLocateSuccessRate(true, result?.geo?.source || "none");
          logInfo(`[LocationService] webloc.getCurrentPosition success: ${JSON.stringify(result)}`);
        }
        if (window?.__PAGE_JS_START__) {
          reportPerfMetric({
            key: "getCurrentLocationStart",
            value: getCurrentLocationStart - window.__PAGE_JS_START__,
            tags: {
              isSuccess: !err,
              source: result?.geo?.source || LocationSource.DEFAULT,
            },
          });
        }
        reportPerfMetric({
          key: "getCurrentLocationCost",
          value: getCurrentLocationEnd - getCurrentLocationStart,
          tags: {
            isSuccess: !err,
            source: result?.geo?.source || LocationSource.DEFAULT,
          },
        });
      });
    });

    return this.currentLocationPromise;
  }

  /**
   * 获取实际城市信息（基于当前位置）
   * @param config 可选配置，用于覆盖默认配置
   * @returns Promise<CityInfo> 城市信息Promise
   */
  public getActualCityInfo(config: Partial<typeof MARS_LOC_CONFIG> = {}): Promise<CityInfo> {
    if (this.actualCityInfoPromise) {
      return this.actualCityInfoPromise;
    }
    this.actualCityInfoPromise = new Promise((resolve) => {
      const getActualCityInfoStart = Date.now();
      this.getCurrentLocation({ config })
        .then((result: LocationData) => {
          // 判断 cityId 非有效值，直接使用兜底北京的经纬度
          const cityId = result?.city?.id;
          if (typeof cityId !== "number" || cityId <= 0) {
            this.actualCityInfo = DEFAULT_LOCATION;
            resolve(DEFAULT_LOCATION);
            reportPerfMetric({
              key: "getActualCityInfoCost",
              value: Date.now() - getActualCityInfoStart,
              tags: {
                status: "loc-success-cityIdInvalid",
                source: result?.geo?.source || LocationSource.DEFAULT,
              },
            });
            return;
          }

          // 真实定位失败的情况下，兜底使用北京的经纬度展示供给
          const lng = result?.geo?.longitude || DEFAULT_LOCATION.lng;
          const lat = result?.geo?.latitude || DEFAULT_LOCATION.lat;
          const cityCode =
            typeof result?.city?.citycode === "string"
              ? result?.city?.citycode
              : DEFAULT_LOCATION.cityCode;

          if (typeof result?.city?.citycode !== "string") {
            // 上报 cityCode 非字符串的情况
            reportError({
              name: "[LocationService] getActualCityInfoError cityCodeInvalid",
              msg: "getActualCityInfoResult:" + JSON.stringify(result),
              level: Level.ERROR,
              category: "baseError",
            });
          }
          // 检查 cityId 是否无效，无效则使用兜底的经纬度
          const isAcutalCityInfoInvalid = checkCityIdInvalid(result?.city?.id);
          const actualCityInfo = {
            cityId: result?.city?.id || DEFAULT_LOCATION.cityId,
            cityCode: cityCode || DEFAULT_LOCATION.cityCode,
            townCode: result?.city?.adcode || DEFAULT_LOCATION.townCode,
            cityName: result?.city?.name || "",
            lng,
            lat,
            wm_lng: Math.round(lng * 1e6),
            wm_lat: Math.round(lat * 1e6),
            detailAddress: result?.city?.address || result?.city?.detail || "",
            source: result?.geo?.source || LocationSource.DEFAULT,
          };
          // SSR 阶段通过 IP 定位获取的实际城市信息
          const ssrActualCityInfo = this.ssrLocationInfo?.actualCityInfo || null;

          // 判断真实定位和 SSR 获取的 IP 定位的 cityId 是否相同
          if (isAcutalCityInfoInvalid) {
            logInfo(`[LocationService] getActualCityInfo真实定位cityId无效`);
            // 1. 真实定位 cityId 无效
            if (ssrActualCityInfo?.cityId) {
              // 1.1 有 IP 定位的数据，使用 IP 定位结果
              this.actualCityInfo = ssrActualCityInfo;
            } else {
              // 1.2 没有 IP 定位的数据，使用兜底城市信息
              this.actualCityInfo = DEFAULT_LOCATION;
            }
          } else {
            // 2. 真实定位 cityId 有效
            logInfo(`[LocationService] getActualCityInfo真实定位cityId有效`);
            if (ssrActualCityInfo?.cityId === actualCityInfo.cityId) {
              // 2.1 cityId 相同，判断经纬度差距是否 > 3km
              // - 超过 3km，使用真实定位刷新页面数据
              // - 不超过 3km，使用 SSR 获取的 IP 定位城市信息，不用刷新页面数据
              const ipLng = ssrActualCityInfo?.lng || 0;
              const ipLat = ssrActualCityInfo?.lat || 0;
              const distance = this.calculateDistance(lng, lat, ipLng, ipLat);
              if (distance > CAMPARE_MAX_DISTANCE) {
                // 2.1.1 超过 3km，使用真实定位刷新页面数据
                this.actualCityInfo = actualCityInfo;
                logInfo(
                  `[LocationService] getActualCityInfo distance > 3km，distance: ${distance}`,
                );
              } else {
                // 2.1.2 不超过 3km，使用 SSR 获取的 IP 定位城市信息，不用刷新页面数据
                this.actualCityInfo = ssrActualCityInfo;
                logInfo(
                  `[LocationService] getActualCityInfo distance <= 3km，distance: ${distance}`,
                );
              }
            } else {
              // 2.2 cityId 不同，使用真实定位刷新页面数据
              this.actualCityInfo = actualCityInfo;
              logInfo(`[LocationService] getActualCityInfo cityId is not same`);
            }
          }
          resolve(this.actualCityInfo);
          reportPerfMetric({
            key: "getActualCityInfoCost",
            value: Date.now() - getActualCityInfoStart,
            tags: {
              status: "loc-success-cityIdValid",
              source: actualCityInfo?.source || LocationSource.DEFAULT,
            },
          });
          logInfo(
            `[LocationService] getActualCityInfo success: ${JSON.stringify(this.actualCityInfo)}`,
          );
          console.log("[LocationService] getActualCityInfo success", this.actualCityInfo);
        })
        .catch((error) => {
          // 定位失败的情况下
          // 1. 存在 IP 定位，使用 IP 定位
          // 2. 不存在 IP 定位，兜底使用北京的经纬度
          reportError({
            name: "[LocationService] getActualCityInfoError",
            msg: error,
            level: Level.ERROR,
            category: "baseError",
          });
          this.actualCityInfo = this.ssrLocationInfo?.actualCityInfo || DEFAULT_LOCATION;
          resolve(this.actualCityInfo);
          console.log("[LocationService] getActualCityInfo fail:", this.actualCityInfo);
          logWarn(
            `[LocationService] getActualCityInfo fail: ${JSON.stringify(this.actualCityInfo)}`,
          );
          reportPerfMetric({
            key: "getActualCityInfoCost",
            value: Date.now() - getActualCityInfoStart,
            tags: {
              status: "locFail",
              source: LocationSource.DEFAULT,
            },
          });
        });
    });
    return this.actualCityInfoPromise;
  }

  /**
   * 获取选中的城市信息（基于URL参数）
   * @returns CityInfo | null 选中的城市信息
   */
  public getSelectCityInfo(): Promise<CityInfo> {
    if (this.selectCityInfoPromise) {
      return this.selectCityInfoPromise;
    }

    this.selectCityInfoPromise = this.getActualCityInfo()
      .then((actualCityInfo) => {
        const getSelectCityInfoStart = Date.now();
        const urlParams = getUrlParams();
        const cityId = urlParams.selCityId ? Number(urlParams.selCityId) : null;
        // 选择城市与实际城市相同，优先使用真实定位的经纬度
        const isSelectActualCity = cityId === actualCityInfo?.cityId;
        const selectLng = Number(urlParams.selLng || 0);
        const selectLat = Number(urlParams.selLat || 0);
        const lng = isSelectActualCity ? actualCityInfo?.lng || selectLng : selectLng;
        const lat = isSelectActualCity ? actualCityInfo?.lat || selectLat : selectLat;
        // 没有手选城市信息时，返回真实定位城市
        this.selectCityInfo = cityId
          ? {
              cityId: cityId,
              cityName: urlParams.selCityName || "",
              lng,
              lat,
              wm_lng: Math.round(lng * 1e6),
              wm_lat: Math.round(lat * 1e6),
            }
          : actualCityInfo;
        reportPerfMetric({
          key: "getSelectCityInfoCost",
          value: Date.now() - getSelectCityInfoStart,
        });
        logInfo(
          `[LocationService] getSelectCityInfo success: ${JSON.stringify(this.selectCityInfo)}`,
        );
        return this.selectCityInfo || DEFAULT_LOCATION;
      })
      .catch(() => {
        return this.actualCityInfo || DEFAULT_LOCATION;
      });
    return this.selectCityInfoPromise;
  }

  public getSSRActualCityInfo(): CityInfo | null {
    return this.ssrLocationInfo?.actualCityInfo || null;
  }

  public getSSRSelectCityInfo(): CityInfo | null {
    return this.ssrLocationInfo?.selectCityInfo || null;
  }

  /**
   * 计算两个经纬度点之间的距离（单位：公里）
   * @param lng1 第一个点的经度
   * @param lat1 第一个点的纬度
   * @param lng2 第二个点的经度
   * @param lat2 第二个点的纬度
   * @returns number 距离（公里）
   */
  private calculateDistance(lng1: number, lat1: number, lng2: number, lat2: number): number {
    if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;
    // 将度转换为弧度
    function toRadians(degrees: number) {
      return (degrees * Math.PI) / 180;
    }

    const R = 6371; // 地球半径，单位为公里
    const x1 = toRadians(lat1);
    const x2 = toRadians(lat2);
    const y1 = toRadians(lat2 - lat1);
    const y2 = toRadians(lng2 - lng1);

    const a =
      Math.sin(y1 / 2) * Math.sin(y1 / 2) +
      Math.cos(x1) * Math.cos(x2) * Math.sin(y2 / 2) * Math.sin(y2 / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c; // 计算距离，单位为公里
    return distance;
  }

  /**
   * 获取实际城市信息同步方法
   * @param config 可选配置，用于覆盖默认配置
   * @returns CityInfo | null 城市信息
   */
  public getActualCityInfoSync(): CityInfo | null {
    return this.actualCityInfo;
  }

  /**
   * 获取选中的城市信息同步方法
   * @returns CityInfo | null 选中的城市信息
   */
  public getSelectCityInfoSync(): CityInfo | null {
    return this.selectCityInfo;
  }

  /**
   * 获取 SSR 预取城市信息与当前城市信息的差异
   * 根据差异信息判断是否要刷新模块数据，各个模块的规则如下：
   *  1. 红包墙：三级城市 ID 不同，需要重新请求数据
   *  2. 导航栏、资源位：cityId 不同，需要重新请求数据
   *  3. 供给列表：cityId 不同，或者经纬度差距 > 3km，需要重新请求数据
   * @returns SSRLocationDiff 差异信息
   */
  public async getSsrDiff(): Promise<SSRLocationDiff> {
    const [actualCityInfo, selectCityInfo] = await Promise.all([
      this.getActualCityInfo(),
      this.getSelectCityInfo(),
    ]);

    const { actualCityInfo: ssrActualCityInfo, selectCityInfo: ssrSelectCityInfo } =
      this.ssrLocationInfo || {};
    logInfo(`[LocationService] getSsrDiff
      actualCityInfo: ${JSON.stringify(actualCityInfo)}
      selectCityInfo: ${JSON.stringify(selectCityInfo)}
      ssrActualCityInfo: ${JSON.stringify(ssrActualCityInfo)}
      ssrSelectCityInfo: ${JSON.stringify(ssrSelectCityInfo)}
    `);
    if (!ssrActualCityInfo || !ssrSelectCityInfo) {
      reportSSRLocateSuccessRate(false, {
        isSameActualCityId: false,
        isSameSelectCityId: false,
        isActualLatLngInRange: false,
        isSelectLatLngInRange: false,
        isSameTownCode: false,
      });
      logInfo(`[LocationService] getSsrDiff ssrActualCityInfo or ssrSelectCityInfo is null`);
      return {
        isSameActualCityId: false,
        isSameSelectCityId: false,
        isActualLatLngInRange: false,
        isSelectLatLngInRange: false,
        isSameTownCode: false,
      };
    }

    const isSameActualCityId = actualCityInfo?.cityId === ssrActualCityInfo.cityId;
    const isSameSelectCityId = selectCityInfo?.cityId === ssrSelectCityInfo.cityId;
    const isActualLatLngInRange = isSameActualCityId
      ? this.calculateDistance(
          actualCityInfo.lng || 0,
          actualCityInfo.lat || 0,
          ssrActualCityInfo.lng,
          ssrActualCityInfo.lat,
        ) <= 3
      : false;
    const isSelectLatLngInRange = isSameSelectCityId
      ? this.calculateDistance(
          selectCityInfo.lng || 0,
          selectCityInfo.lat || 0,
          ssrSelectCityInfo.lng,
          ssrSelectCityInfo.lat,
        ) <= 3
      : false;
    const isSameTownCode = actualCityInfo?.townCode === ssrActualCityInfo.townCode;

    console.log("[LocationService] getSsrDiff result", {
      isSameActualCityId,
      isSameSelectCityId,
      isActualLatLngInRange,
      isSelectLatLngInRange,
      isSameTownCode,
    });
    logInfo(
      `[LocationService] getSsrDiff result: ${JSON.stringify({
        isSameActualCityId,
        isSameSelectCityId,
        isActualLatLngInRange,
        isSelectLatLngInRange,
        isSameTownCode,
      })}`,
    );

    reportSSRLocateSuccessRate(true, {
      isSameActualCityId,
      isSameSelectCityId,
      isActualLatLngInRange,
      isSelectLatLngInRange,
      isSameTownCode,
    });

    if (CURRENT_CODE_ENV !== CODE_ENV.PRODUCTION) {
      // mock IP 定位数据是否相同
      const urlParams = getUrlParams();
      return {
        isSameActualCityId:
          urlParams.testSameCityId === "1"
            ? true
            : urlParams.testSameCityId === "0"
              ? false
              : isSameActualCityId,
        isSameSelectCityId:
          urlParams.testSameCityId === "1"
            ? true
            : urlParams.testSameCityId === "0"
              ? false
              : isSameSelectCityId,
        isActualLatLngInRange:
          urlParams.testLatLngInRange === "1"
            ? true
            : urlParams.testLatLngInRange === "0"
              ? false
              : isActualLatLngInRange,
        isSelectLatLngInRange:
          urlParams.testLatLngInRange === "1"
            ? true
            : urlParams.testLatLngInRange === "0"
              ? false
              : isSelectLatLngInRange,
        isSameTownCode:
          urlParams.testSameTownCode === "1"
            ? true
            : urlParams.testSameTownCode === "0"
              ? false
              : isSameTownCode,
      };
    }

    return {
      isSameActualCityId,
      isSameSelectCityId,
      isActualLatLngInRange,
      isSelectLatLngInRange,
      isSameTownCode,
    };
  }
}

const locationService = new LocationService();
export default locationService;

/**
 * CSR 初始化位置信息
 * @returns Promise<LocationInfo | null> 位置信息 Promise
 */
let locationPromise: Promise<LocationInfo | null> | null = null;
let locationInfo: LocationInfo | null = null;
export function getLocationInfo(): Promise<LocationInfo | null> {
  if (locationPromise) {
    return locationPromise;
  }
  locationPromise = new Promise(async (resolve) => {
    try {
      if (isServer) {
        // SSR 场景
        locationInfo = null;
        resolve(null);
      } else {
        // CSR 场景
        const actualCityInfo = await locationService.getActualCityInfo();
        const selectCityInfo = await locationService.getSelectCityInfo();
        const ssrDiff = await locationService.getSsrDiff();
        // mock 定位较慢的情况
        // await new Promise((resolve) => setTimeout(resolve, 5000));
        locationInfo = {
          actualCityInfo,
          selectCityInfo,
          ssrDiff,
          isInited: true,
          source: actualCityInfo?.source || LocationSource.DEFAULT,
        };
        logInfo(`[locationService] getLocationInfo: ${JSON.stringify(locationInfo)}`);
        resolve(locationInfo);
      }
    } catch (error) {
      reportError({
        name: "[location] fetchLocationInfoError",
        msg: error,
        category: "resourceError",
        level: Level.WARN,
      });
      locationInfo = null;
      resolve(null);
    }
  });
  return locationPromise;
}
export function getCacheLocationInfo(): LocationInfo | null {
  return locationInfo;
}

/** IPv4 格式：四段数字用点分隔 */
const IPV4_REGEX = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
/** IPv6 格式：完整形式 8 组十六进制（如 2408:8409:2530:9143:8083:f8a2:dbac:604a），或含 :: 的压缩形式 */
const IPV6_FULL_REGEX = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
const IPV6_COMPRESSED_REGEX =
  /^([0-9a-fA-F]{1,4}:){1,7}:|^([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|^([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|^([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|^([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|^([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|^[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|^:((:[0-9a-fA-F]{1,4}){1,7}|:)$/;

/**
 * 校验是否为合法 IP（IPv4 或 IPv6 均可）
 */
function isValidIPAddress(ip: string): boolean {
  return IPV4_REGEX.test(ip) || IPV6_FULL_REGEX.test(ip) || IPV6_COMPRESSED_REGEX.test(ip);
}

/** 经纬度定位可选参数：URL 中的坐标系标记 */
export interface GetCityInfoByLatLngOptions {
  geotypeFromQuery?: string; // "WGS84" | "GCJ02" | "BD09"
  coordinatesFromQuery?: string; // "0" | "1" | "2"
}

const COORDINATES_VALID = ["0", "1", "2"] as const;

/**
 * 将 URL 的 geotype/coordinates 映射为 divisionopencity 接口的 coordinates 参数
 * 默认 WGS84
 */
function resolveCoordinates(options?: GetCityInfoByLatLngOptions): string {
  const geo = options?.geotypeFromQuery?.toUpperCase();
  if (geo === "WGS84") return "0";
  if (geo === "GCJ02") return "1";
  if (geo === "BD09") return "2";
  const coord = options?.coordinatesFromQuery;
  if (coord && COORDINATES_VALID.includes(coord as any)) return coord;
  return "0";
}

/**
 * SSR 通过经纬度获取城市信息（开站城市与行政区域查询）
 * 失败时返回 DEFAULT_LOCATION，不回退 IP 定位
 * @param lng 经度
 * @param lat 纬度
 * @param options URL 中的 geotype/coordinates，用于坐标系映射
 */
export async function getCityInfoByLatLngForSSR(
  lng: number,
  lat: number,
  options?: GetCityInfoByLatLngOptions,
): Promise<CityInfo> {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return DEFAULT_LOCATION;
  }
  const coordinates = resolveCoordinates(options);
  // 接口要求：经纬度以 "," 分割，小数点后不得超过 6 位，"lng,lat" 经度在前纬度在后
  const location = `${Number(lng).toFixed(6)},${Number(lat).toFixed(6)}`;
  // 与 IP 定位对齐：division 对应行政区域，mt_front_info 对应开站城市 ID/名称
  const show_fields = "division|mt_front_info|mt_back_info|location_tag";

  const res = await fetchCityInfoByLatLng({
    key: IP_LOCATE_KEY || "",
    location,
    coordinates,
    show_fields,
  });

  if (!res || res.status !== 200 || !res.result) {
    return DEFAULT_LOCATION;
  }

  const { division, mt_front_info, mt_back_info } = res.result;

  // 优先从 division 组装行政相关字段（与 IP 定位的 ip_reverse_address 对应）
  const cityCode = division?.citycode ?? mt_back_info?.citycode ?? DEFAULT_LOCATION.cityCode;
  const townCode =
    division?.adcode ?? division?.towncode ?? mt_back_info?.adcode ?? DEFAULT_LOCATION.townCode;
  const detailAddress = division?.district || division?.city || "";

  // cityId、cityName 来自开站城市信息（mt_front_info），无则尝试 mt_back_info
  const cityId = Number(mt_front_info?.mt_front_city_id ?? mt_back_info?.mt_back_city_id ?? 0);
  if (checkCityIdInvalid(cityId)) {
    // 仅有 division 无开站城市 ID 时，用 division 的城市名兜底展示，但 cityId 无效仍走默认
    console.log(
      "[LocationService] getCityInfoByLatLngForSSR cityId invalid (no mt_front_info/mt_back_info), use default",
    );
    return DEFAULT_LOCATION;
  }

  const cityName =
    mt_front_info?.mt_front_city_name ?? mt_back_info?.mt_back_city_name ?? division?.city ?? "";

  const cityInfo: CityInfo = {
    cityId,
    cityName,
    cityCode: cityCode ?? DEFAULT_LOCATION.cityCode,
    townCode: townCode ?? DEFAULT_LOCATION.townCode,
    lng,
    lat,
    wm_lng: Math.round(lng * 1e6),
    wm_lat: Math.round(lat * 1e6),
    detailAddress: detailAddress || undefined,
    source: LocationSource.COORD,
  };
  console.log("[LocationService] getCityInfoByLatLngForSSR success", cityInfo);
  return cityInfo;
}

/**
 * SSR 获取 IP 定位城市信息
 * @param ip IP地址（支持 IPv4 或 IPv6）
 * @returns Promise<CityInfo | null> 城市信息Promise
 */
export async function getIPCityInfo(ip: string): Promise<CityInfo> {
  try {
    if (!isValidIPAddress(ip)) {
      console.log("[LocationService] getIPCityInfo ip is invalid (expect IPv4 or IPv6)");
      return DEFAULT_LOCATION;
    }

    const res = await fetchIPLocation({ ip, key: IP_LOCATE_KEY || "" });
    console.log("[LocationService] getIPCityInfo ipLocation", res);
    // 判断 cityId 不合法的情况，使用兜底北京的城市信息
    if (checkCityIdInvalid(res?.mt_city_station?.id)) {
      console.log("[LocationService] getIPCityInfo cityId invalid, use default");
      return DEFAULT_LOCATION;
    }

    const lng = res?.ip_location?.lng || DEFAULT_LOCATION.lng;
    const lat = res?.ip_location?.lat || DEFAULT_LOCATION.lat;
    const townCode = res?.ip_reverse_address?.adcode;
    const ipCityInfo = {
      cityId: res?.mt_city_station?.id || DEFAULT_LOCATION.cityId,
      cityCode: townCode || DEFAULT_LOCATION.cityCode,
      townCode: townCode || DEFAULT_LOCATION.townCode,
      cityName: res?.mt_city_station?.name || "",
      lng,
      lat,
      wm_lng: Math.round(lng * 1e6),
      wm_lat: Math.round(lat * 1e6),
      detailAddress: res?.ip_reverse_address?.district || res?.ip_reverse_address?.city || "",
      source: LocationSource.IP,
    };
    console.log("[LocationService] getIPCityInfo success", ipCityInfo);
    return ipCityInfo;
  } catch {
    return DEFAULT_LOCATION;
  }
}

/**
 * SSR 获取选择城市信息
 * @param query 请求参数
 * @param ipCityInfo IP定位城市信息
 * @returns
 */
export function getSelectCityInfoForSSR(query: Record<string, any>, ipCityInfo: CityInfo) {
  const cityId = query.selCityId ? Number(query.selCityId) : null;
  // 选择城市与实际城市相同，优先使用真实定位的经纬度
  const isSelectActualCity = cityId === ipCityInfo?.cityId;
  const selectLng = Number(query.selLng || 0);
  const selectLat = Number(query.selLat || 0);
  const lng = isSelectActualCity ? ipCityInfo?.lng || selectLng : selectLng;
  const lat = isSelectActualCity ? ipCityInfo?.lat || selectLat : selectLat;
  // 没有手选城市信息时，返回真实定位城市
  const selectCityInfo = cityId
    ? {
        cityId: cityId,
        cityName: query.selCityName || "",
        lng,
        lat,
        wm_lng: Math.round(lng * 1e6),
        wm_lat: Math.round(lat * 1e6),
      }
    : ipCityInfo;
  return selectCityInfo;
}

/**
 * SSR 环境下统一的定位获取函数
 *
 * @description
 * 封装 SSR 环境下的定位获取逻辑，自动判断使用经纬度定位还是 IP 定位
 *
 * @param params - 定位参数
 * @returns Promise<CityInfo> 城市信息
 *
 * @example
 * // 在 middleware 中使用（有 URL 参数和 request 对象）
 * const cityInfo = await fetchLocationForSSR({
 *   lat: urlParams.get("lat"),
 *   lng: urlParams.get("lng"),
 *   geotype: urlParams.get("geotype"),
 *   coordinates: urlParams.get("coordinates"),
 *   ip: request.ip || "",
 * });
 *
 * @example
 * // 在 getServerSideProps 中使用（有 query 和 params.context）
 * const cityInfo = await fetchLocationForSSR({
 *   lat: query?.lat,
 *   lng: query?.lng,
 *   geotype: query?.geotype,
 *   coordinates: query?.coordinates,
 *   ip: String(params?.context?.ip || ""),
 * });
 */
export async function fetchLocationForSSR(params: {
  lat?: string | null;
  lng?: string | null;
  geotype?: string | null;
  coordinates?: string | null;
  ip?: string;
}): Promise<CityInfo> {
  const { lat, lng, geotype, coordinates, ip } = params;

  // 解析经纬度参数
  const latFromQuery = lat != null ? Number(lat) : NaN;
  const lngFromQuery = lng != null ? Number(lng) : NaN;

  // 判断是否使用经纬度定位
  const hasValidLatLng =
    ENABLE_SSR_COORD_LOCATE && Number.isFinite(latFromQuery) && Number.isFinite(lngFromQuery);

  if (hasValidLatLng) {
    // 经纬度定位：优先使用 divisionopencity，失败用兜底，不回退 IP
    console.log(
      `[fetchLocationForSSR] Using coordinate location: lat=${latFromQuery}, lng=${lngFromQuery}`,
    );
    return await getCityInfoByLatLngForSSR(lngFromQuery, latFromQuery, {
      geotypeFromQuery: geotype ?? undefined,
      coordinatesFromQuery: coordinates ?? undefined,
    }).catch((error) => {
      console.log(
        `[fetchLocationForSSR] Coordinate location failed, using DEFAULT_LOCATION: ${error}`,
      );
      return DEFAULT_LOCATION;
    });
  } else {
    // IP 定位
    const ipStr = String(ip || "");
    console.log(`[fetchLocationForSSR] Using IP location: ip="${ipStr}"`);
    return await getIPCityInfo(ipStr).catch((error) => {
      console.log(`[fetchLocationForSSR] IP location failed, using DEFAULT_LOCATION: ${error}`);
      return DEFAULT_LOCATION;
    });
  }
}
